import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import { Main } from "@/features/standard/Main";
import customAlert from "@/features/ui/alert/alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { getUserCodeByUser } from "@/utils/checkData";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Select, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrdersPurchaseOrderIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign({}, Main, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  DateInput: DateInput,
});

interface PurchaseOrderUpdateFormProps {
  formatterProps: PurchaseOrdersGet200ResponseRowsInner | undefined;
}

export const OrderUpdateForm = (params: PurchaseOrderUpdateFormProps) => {
  const { formatterProps } = params;
  const userCode = getUserCodeByUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: updateMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdPutRequest) =>
      mutatePurchaseOrders
        .update(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrders"]);
        closeModal(formatterProps);
        customAlert(
          `${userCode === "FLOUR" ? t("message.editSuccess", { item: t("purchaseOrder.purchaseOrder") }) : t("message.editSuccess", { item: t("order.order") })}`,
          `${userCode === "FLOUR" ? t("common.editSuccess", { item: t("purchaseOrder.purchaseOrder") }) : t("common.editSuccess", { item: t("order.order") })}`,
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: `${userCode === "FLOUR" ? t("purchaseOrder.purchaseOrder") : t("order.order")}`,
          }),
          `${userCode === "FLOUR" ? t("common.editFail", { item: t("purchaseOrder.purchaseOrder") }) : t("common.editFail", { item: t("order.order") })}`,
          "red",
        );
      },
    },
  );

  const { closeModal } = useModal();

  const form = useForm({
    initialValues: {
      partnerCode: formatterProps?.partnerCode,
      status: formatterProps?.status,
      userCode: formatterProps?.userCode,
      code: formatterProps?.code,
      targetLocationCode: formatterProps?.targetLocationCode,
      orderDate: formatterProps?.orderDate ? new Date(formatterProps.orderDate) : null,
      deliveryDeadline: formatterProps?.deliveryDeadline
        ? new Date(formatterProps.deliveryDeadline)
        : null,
      // quantityOrdered: formatterProps.quantityOrdered,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        purchaseOrderId: formatterProps?.id ?? 0,
        purchaseOrdersGetRequest: {
          direction: "OUTBOUND",
          partnerCode: form.values.partnerCode!,
          status: form.values.status!,
          code: form.values.code!,
          // quantityOrdered : form.values.quantityOrdered!,
          userCode: form.values.userCode!,
          targetLocationCode: form.values.targetLocationCode!,
          orderDate: form.values.orderDate?.toDateString()!,
          deliveryDeadline: form.values.deliveryDeadline?.toDateString()!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({
          error,
          errorContext: `${userCode === "FLOUR" ? t("purchaseOrder.purchaseOrder") : t("order.order")}`,
        }),
        `${userCode === "FLOUR" ? t("common.editFail", { item: t("purchaseOrder.purchaseOrder") }) : t("common.editFail", { item: t("order.order") })}`,
        "red",
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          defaultValue={formatterProps?.partnerCode}
          {...form.getInputProps("partnerCode")}
          error={form.isDirty("partnerCode") ? t("common.toSave") : ""}
        />
        <Select
          label={`${userCode === "FLOUR" ? t("purchaseOrder.status") : t("order.status")}`}
          placeholder={`${userCode === "FLOUR" ? t("placeholder.select", { item: t("purchaseOrder.status") }) : t("placeholder.select", { item: t("order.status") })}`}
          data={[
            { value: "DRAFT", label: t("purchaseStatus.DRAFT") },
            { value: "IN_PROGRESS", label: t("purchaseStatus.IN_PROGRESS") },
            { value: "DONE", label: t("purchaseStatus.DONE") },
            { value: "CANCELED", label: t("purchaseStatus.CANCELED") },
          ]}
          defaultValue={formatterProps?.status}
          {...form.getInputProps("status")}
          error={form.isDirty("status") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("status") ? "red !important" : "",
              borderColor: form.isDirty("status") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          label={`${userCode === "FLOUR" ? t("purchaseOrder.code") : t("order.code")}`}
          placeholder={`${userCode === "FLOUR" ? t("placeholder.select", { item: t("purchaseOrder.code") }) : t("placeholder.select", { item: t("order.code") })}`}
          defaultValue={formatterProps?.code}
          {...form.getInputProps("code")}
          error={form.isDirty("code") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("code") ? "red !important" : "",
              borderColor: form.isDirty("code") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        {/* <Update.Input
                    label="발주항목 갯수"
                    placeholder="발주항목 갯수 입력"
                    defaultValue={formatterProps.quantityOrdered}
                    {...form.getInputProps("quantityOrdered")}
                    error={form.isDirty("quantityOrdered") ? "저장 필요" : ""}
                    styles={{
                        input: {
                            color: form.isDirty("quantityOrdered") ? "cadetblue !important" : "inherit",
                            borderColor: form.isDirty("quantityOrdered") ? "cadetblue !important" : "inherit",
                        },
                        error: { fontSize: 11, color: "cadetblue" }
                    }}
                /> */}
        <UserAutoComplete
          label={t("order.manager")}
          placeholder={t("placeholder.select", { item: t("order.manager") })}
          defaultValue={formatterProps?.userCode}
          {...form.getInputProps("userCode")}
          error={form.isDirty("userCode") ? t("common.toSave") : ""}
        />
        <LocationAutoComplete
          label={t("inventory.toLocation")}
          placeholder={t("placeholder.select", { item: t("inventory.toLocation") })}
          defaultValue={formatterProps?.targetLocationCode}
          {...form.getInputProps("targetLocationCode")}
          error={form.isDirty("targetLocationCode") ? t("common.toSave") : ""}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={`${userCode === "FLOUR" ? t("purchaseOrder.purchaseOrderDate") : t("order.orderDate")}`}
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("orderDate")}
          error={form.isDirty("orderDate") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("orderDate") ? "red !important" : "",
              borderColor: form.isDirty("orderDate") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("order.deliveryDate")}
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("deliveryDeadline")}
          error={form.isDirty("deliveryDeadline") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("deliveryDeadline") ? "red !important" : "",
              borderColor: form.isDirty("deliveryDeadline") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button
            disabled={validateFormValues({
              pageType: "purchaseOrder",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {userCode === "FLOUR"
              ? t("common.edit", { item: t("purchaseOrder.purchaseOrder") })
              : t("common.edit", { item: t("order.order") })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
